import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'
import { ParsedUrlQuery } from 'querystring'

import { collectionPageModuleQuery } from '@data/sanity/queries/page'
import { SanityCollectionPage } from '@data/sanity/queries/types/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getShopifyDomain } from '@lib/shopify/client'
import { getPageSanityClient, getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getAllSlugs,
  getCollectionPage,
} from '@lib/sanity/page'
import { getAllLocales, Locale } from '@lib/language'
import { getReviews, Reviews } from '@lib/review'
import { CustomStaticPropsContext, StaticPathWithSlug } from '@lib/routes'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import PreviewModules from '@modules/preview-modules'

const PreviewProvider = dynamic(() => import('@lib/sanity/preview'))

interface CollectionPageProps {
  draftMode: boolean
  draftToken?: string
  locale: Locale
  slug: string
  page: SanityCollectionPage
  site: SanitySiteFragment
  reviews?: Reviews
}

const CollectionPage = ({
  draftMode,
  draftToken,
  locale,
  slug,
  page,
  site,
  reviews,
}: CollectionPageProps) => {
  if (draftMode && draftToken) {
    return (
      <Layout page={page} site={site}>
        <PreviewProvider draftToken={draftToken}>
          <PreviewModules
            modules={page.modules}
            moduleQuery={collectionPageModuleQuery}
            moduleQueryParams={{
              ...defaultModuleQueryParameters,
              locale,
              collectionSlug: slug,
            }}
            reviews={reviews}
          />
        </PreviewProvider>
      </Layout>
    )
  }

  return (
    <Layout page={page} site={site}>
      <Modules modules={page.modules} reviews={reviews} />
    </Layout>
  )
}

interface StaticCollectionPageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps(
  context: CustomStaticPropsContext<StaticCollectionPageParams>,
): Promise<GetStaticPropsResult<CollectionPageProps>> {
  const [sanityClient, draftMode, draftToken] = getPageSanityClient(
    context.draftMode,
  )
  const slug = context.params?.slug ?? ''
  const collectionPage = await getCollectionPage(
    sanityClient,
    context.locale,
    slug,
  )

  if (!collectionPage.page) {
    return {
      notFound: true,
    }
  }

  // Get Stamped.io reviews
  let reviews: Reviews = {
    rating: 0,
    items: [],
  }

  try {
    const shopifyDomain = getShopifyDomain(context.locale)
    reviews = await getReviews(
      collectionPage.page.modules ?? [],
      shopifyDomain,
      collectionPage.site.settings.stampedApiKey,
    )
  } catch (error) {
    console.error(error)
  }

  return {
    props: {
      draftMode,
      draftToken,
      locale: context.locale,
      slug,
      page: collectionPage.page,
      site: collectionPage.site,
      reviews,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticCollectionPageParams>
> {
  const sanityClient = getSanityClient()
  const allCollections = await getAllSlugs(sanityClient, 'collection')
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlug[] = allCollections
    .map((collection) => ({
      ...collection,
      locale: collection.locale as Locale,
    }))
    .filter(({ locale }) => allLocales.includes(locale))
    .filter(({ slug }) => !!slug)
    .map(({ locale, slug }) => ({
      params: {
        slug,
      },
      locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default CollectionPage
